import {calculateErrorMessage} from 'common/helpers'
import {WAVRecorder} from 'common/helpers/wavRecorder'
import {useCallback, useMemo, useRef} from 'react'

import {createAudioPlayer} from '../voice/helpers/audioPlayback'
import {createProgressiveTranscriptManager} from '../voice/helpers/progressiveTranscript'
import {mapWordsToTimestamps} from '../voice/helpers/voiceUtils'
import {TranscriptLine, VoiceCallStatus} from './useVoiceCallState'
import {VoiceCallSharedRefs} from './useVoiceCallStateRegistry'

export const useVoiceCallAudio = (
  refs: VoiceCallSharedRefs,
  stateActions: {
    setStatus: (s: VoiceCallStatus) => void
    setIsAiSpeaking: (s: boolean) => void
    setAwaitingResponse: (s: boolean) => void
    setTranscript: (fn: (prev: TranscriptLine[]) => TranscriptLine[]) => void
  }
) => {
  const {setStatus, setIsAiSpeaking, setAwaitingResponse, setTranscript} = stateActions

  const wavRecorderRef = useRef<WAVRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const audioPlayerRef = useRef(createAudioPlayer())
  const audioPlaybackRef = useRef<HTMLAudioElement | null>(null)
  const fillerAudioRef = useRef<HTMLAudioElement | null>(null)
  const fillerAudioUrlRef = useRef<string | null>(null)
  const answerAudioUrlRef = useRef<string | null>(null)
  const fillerAudioBlobRef = useRef<Blob | null>(null)
  const answerAudioToPlayRef = useRef<Blob | null>(null)
  const answerCompletionCallbackRef = useRef<((r: string, imm?: boolean) => void) | null>(null)
  const fillerTranscriptIdRef = useRef<string | null>(null)
  const fillerTextRef = useRef<string | null>(null)
  const answerTextRef = useRef<string | null>(null)
  const answerTranscriptIdRef = useRef<string | null>(null)

  const progressiveManagerRef = useRef(
    createProgressiveTranscriptManager((updates) => {
      setTranscript((prev) => {
        let next = [...prev]
        for (const u of updates) {
          const idx = next.findIndex((l) => l.id === u.id)
          if (idx >= 0) {
            next = next.map((l) => (l.id === u.id ? {...l, ...u} : l))
          } else {
            next.push({
              id: u.id,
              speaker: (u as any).speaker || 'ai',
              text: u.text || u.progressiveText || '',
              progressiveText: u.progressiveText,
              totalText: (u as any).totalText,
              isComplete: !!u.isComplete,
              timestamp: Date.now(),
            })
          }
        }
        return next
      })
    })
  )

  const startProgressiveTranscript = useCallback((text: string) => {
    if (!text.trim()) return null
    try {
      return progressiveManagerRef.current?.start(text) || null
    } catch (e) {
      calculateErrorMessage(e)
      return null
    }
  }, [])

  const stopProgressiveTranscript = useCallback(() => {
    try {
      progressiveManagerRef.current?.stop()
    } catch (e) {
      calculateErrorMessage(e)
    }
  }, [])

  const stopAudioPlayback = useCallback(() => {
    try {
      audioPlayerRef.current?.stop()
    } catch (e) {
      calculateErrorMessage(e)
    }
    audioPlaybackRef.current = null
  }, [])

  const stopFillerAudio = useCallback(() => {
    try {
      audioPlayerRef.current?.stop()
      fillerAudioRef.current = null
      if (fillerAudioUrlRef.current) {
        URL.revokeObjectURL(fillerAudioUrlRef.current)
        fillerAudioUrlRef.current = null
      }
    } finally {
      answerAudioToPlayRef.current = null
      fillerTranscriptIdRef.current = null
    }
  }, [])

  const syncTranscriptToAudio = useCallback(
    (audio: HTMLAudioElement, transcriptId: string, fullText: string) => {
      if (!audio || !transcriptId || !fullText) return

      const startSync = () => {
        progressiveManagerRef.current?.stop()

        const duration = audio.duration || 0
        if (!duration || isNaN(duration) || duration <= 0) return

        const wordTimes = mapWordsToTimestamps(fullText, duration)
        let lastIndex = -1
        let rafId: number | null = null

        const tick = () => {
          const t = audio.currentTime
          while (lastIndex + 1 < wordTimes.length && wordTimes[lastIndex + 1].time <= t) lastIndex++
          const progressiveText = wordTimes
            .slice(0, lastIndex + 1)
            .map((w) => w.word)
            .join(' ')

          setTranscript((prev) =>
            prev.map((line) =>
              line.id === transcriptId ? {...line, text: progressiveText, progressiveText} : line
            )
          )

          if (audio.ended) {
            setTranscript((prev) =>
              prev.map((line) =>
                line.id === transcriptId ? {...line, text: fullText, isComplete: true} : line
              )
            )
            if (rafId) cancelAnimationFrame(rafId)
            return
          }
          rafId = requestAnimationFrame(tick)
        }

        audio.addEventListener(
          'ended',
          () => {
            if (rafId) cancelAnimationFrame(rafId)
          },
          {once: true}
        )

        rafId = requestAnimationFrame(tick)
      }

      if (audio.readyState >= 1) startSync()
      else audio.addEventListener('loadedmetadata', startSync, {once: true})
    },
    [setTranscript]
  )

  const playFullResponseAudio = useCallback(
    async (blob: Blob, unblockListening: (r: string, imm?: boolean) => void) => {
      if (!(blob instanceof Blob)) return

      setStatus('responding')
      setIsAiSpeaking(true)

      try {
        answerAudioToPlayRef.current = blob
        try {
          if (!answerAudioUrlRef.current && blob) {
            try {
              answerAudioUrlRef.current = URL.createObjectURL(blob)
            } catch (err) {
              calculateErrorMessage(err)
            }
          }
        } catch (e) {
          calculateErrorMessage(e)
        }
        const audio = await audioPlayerRef.current.playBlob(blob, {
          onEnded: () => {
            audioPlaybackRef.current = null
            answerAudioToPlayRef.current = null
            setIsAiSpeaking(false)
            setStatus('listening')
            unblockListening('full response audio ended', true)
          },
          onError: (e) => {
            calculateErrorMessage(e)
            audioPlaybackRef.current = null
            answerAudioToPlayRef.current = null
            setIsAiSpeaking(false)
            setStatus('listening')
            unblockListening('full response audio error', true)
          },
        })
        audioPlaybackRef.current = audio as HTMLAudioElement

        if (answerTextRef.current) {
          const id = startProgressiveTranscript(answerTextRef.current)
          if (id) answerTranscriptIdRef.current = id
        }

        if (answerTranscriptIdRef.current && answerTextRef.current) {
          syncTranscriptToAudio(
            audio as HTMLAudioElement,
            answerTranscriptIdRef.current,
            answerTextRef.current
          )
        }
      } catch (e) {
        answerAudioToPlayRef.current = null
        calculateErrorMessage(e)
        setIsAiSpeaking(false)
        setStatus('listening')
        unblockListening('full response audio play() error', true)
      }
    },
    [setStatus, setIsAiSpeaking, startProgressiveTranscript, syncTranscriptToAudio]
  )

  const playFillerAudio = useCallback(
    async (blob: Blob, unblockListening?: (r: string, imm?: boolean) => void) => {
      if (!(blob instanceof Blob)) return

      setStatus('responding')
      setIsAiSpeaking(true)
      setAwaitingResponse(false)

      try {
        fillerAudioBlobRef.current = blob
        try {
          if (!fillerAudioUrlRef.current && blob) {
            try {
              fillerAudioUrlRef.current = URL.createObjectURL(blob)
            } catch (err) {
              calculateErrorMessage(err)
            }
          }
        } catch (e) {
          calculateErrorMessage(e)
        }
        const audio = await audioPlayerRef.current.playBlob(blob, {
          onEnded: () => {
            fillerAudioRef.current = null
            fillerAudioBlobRef.current = null
            if (answerAudioToPlayRef.current) {
              const audioToPlay = answerAudioToPlayRef.current
              const text = answerTextRef.current

              answerAudioToPlayRef.current = null
              stopProgressiveTranscript()
              let transcriptId: string | null = null
              if (text) {
                transcriptId = startProgressiveTranscript(text)
                answerTranscriptIdRef.current = transcriptId
              }

              audioPlayerRef.current
                .playBlob(audioToPlay, {
                  onEnded: () => {
                    audioPlaybackRef.current = null
                    setIsAiSpeaking(false)
                    setStatus('listening')
                    if (answerCompletionCallbackRef.current) {
                      answerCompletionCallbackRef.current('answer done', true)
                      answerCompletionCallbackRef.current = null
                    } else if (unblockListening) {
                      unblockListening('answer done', true)
                    }
                  },
                })
                .then((ansAudio) => {
                  audioPlaybackRef.current = ansAudio as HTMLAudioElement
                  setIsAiSpeaking(true)
                  setStatus('responding')
                  if (transcriptId && text) {
                    syncTranscriptToAudio(ansAudio as HTMLAudioElement, transcriptId, text)
                  }
                })
                .catch((err) => {
                  calculateErrorMessage(err)
                  audioPlaybackRef.current = null
                  setIsAiSpeaking(false)
                  setStatus('listening')
                  if (answerCompletionCallbackRef.current) {
                    answerCompletionCallbackRef.current('answer play error', true)
                    answerCompletionCallbackRef.current = null
                  } else if (unblockListening) {
                    unblockListening('answer play error', true)
                  }
                })
            }
          },
          onError: (e) => {
            calculateErrorMessage(e)
            fillerAudioRef.current = null
            fillerAudioBlobRef.current = null
            setIsAiSpeaking(false)
            setStatus('listening')
            if (unblockListening) unblockListening('filler play error', true)
          },
        })
        fillerAudioRef.current = audio as HTMLAudioElement
        audioPlaybackRef.current = audio as HTMLAudioElement

        if (
          refs.transcriptionEnabledRef.current === true &&
          fillerTranscriptIdRef.current &&
          fillerTextRef.current
        ) {
          syncTranscriptToAudio(
            audio as HTMLAudioElement,
            fillerTranscriptIdRef.current,
            fillerTextRef.current
          )
        }
      } catch (err) {
        calculateErrorMessage(err)
        setIsAiSpeaking(false)
        setStatus('listening')
        if (unblockListening) unblockListening('filler catch error', true)
      }
    },
    [
      setStatus,
      setIsAiSpeaking,
      setAwaitingResponse,
      refs.transcriptionEnabledRef,
      stopProgressiveTranscript,
      startProgressiveTranscript,
      syncTranscriptToAudio,
    ]
  )

  const startUtteranceRecording = useCallback(() => {
    const status = refs.statusRef.current
    const allowedStatuses = ['connecting', 'listening']
    if (refs.walkthroughActiveRef.current) return
    if (!allowedStatuses.includes(status)) return
    if (wavRecorderRef.current?.getState().isRecording) return

    audioChunksRef.current = []
    refs.hasVoiceActivityRef.current = false
    refs.sustainedSpeechRef.current = false
    refs.realSpeechDetectedRef.current = false
    refs.responseRequestedRef.current = false
    refs.voiceActivityBufferRef.current = []
    refs.lastAudioActivityRef.current = Date.now()
    refs.maxListeningTimeRef.current = Date.now()

    refs.timeoutIdsRef.current.forEach((id) => clearTimeout(id))
    refs.timeoutIdsRef.current.clear()

    try {
      const recorder = new WAVRecorder({
        sampleRate: 16000,
        channels: 1,
        bitDepth: 16,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      })
      wavRecorderRef.current = recorder
      recorder.start().catch(() => {})
    } catch (e) {
      calculateErrorMessage(e)
    }
  }, [refs])

  const stopUtteranceRecording = useCallback(() => {
    const recorder = wavRecorderRef.current
    if (!recorder || !recorder.getState().isRecording) {
      return Promise.resolve({blob: new Blob([], {type: 'audio/wav'}), mimeType: 'audio/wav'})
    }

    return recorder
      .stop()
      .then((blob) => {
        wavRecorderRef.current = null
        return {blob, mimeType: 'audio/wav'}
      })
      .catch(() => {
        wavRecorderRef.current = null
        return {blob: new Blob([], {type: 'audio/wav'}), mimeType: 'audio/wav'}
      })
  }, [])

  const stopRecordingImmediate = useCallback(() => {
    try {
      if (wavRecorderRef.current?.getState().isRecording) wavRecorderRef.current.cancel()
    } catch (e) {
      calculateErrorMessage(e)
    }
    wavRecorderRef.current = null
    audioChunksRef.current = []
  }, [])

  const pausePlayback = useCallback(() => {
    try {
      const audio = audioPlaybackRef.current
      if (!audio) return {type: 'none' as const}
      const position = audio.currentTime || 0
      audio.pause()

      let playbackType: 'full' | 'filler' | 'unknown' = 'unknown'
      let blob: Blob | null = null
      let transcriptId: string | null = null
      let text: string | null = null

      if (answerAudioToPlayRef.current) {
        playbackType = 'full'
        blob = answerAudioToPlayRef.current
        transcriptId = answerTranscriptIdRef.current
        text = answerTextRef.current
      } else if (fillerAudioBlobRef.current) {
        playbackType = 'filler'
        blob = fillerAudioBlobRef.current
        transcriptId = fillerTranscriptIdRef.current
        text = fillerTextRef.current
      } else {
        playbackType = 'unknown'
      }

      try {
        if (playbackType === 'full' && blob && !answerAudioUrlRef.current) {
          try {
            answerAudioUrlRef.current = URL.createObjectURL(blob)
          } catch (e) {
            calculateErrorMessage(e)
          }
        }
        if (playbackType === 'filler' && blob && !fillerAudioUrlRef.current) {
          try {
            fillerAudioUrlRef.current = URL.createObjectURL(blob)
          } catch (e) {
            calculateErrorMessage(e)
          }
        }
      } catch (e) {
        calculateErrorMessage(e)
      }

      const result = {
        type: playbackType,
        blob,
        position,
        transcriptId,
        text,
        queuedAnswerBlob: answerAudioToPlayRef.current,
        url: answerAudioUrlRef.current || fillerAudioUrlRef.current || undefined,
        queuedAnswerUrl: answerAudioUrlRef.current || undefined,
      }

      return result
    } catch (e) {
      calculateErrorMessage(e)
      return {type: 'none' as const}
    }
  }, [])

  const resumePlayback = useCallback(
    async (playback: {
      type: 'full' | 'filler' | 'unknown' | 'none'
      blob?: Blob | null
      url?: string | null
      position?: number
      transcriptId?: string | null
      text?: string | null
      queuedAnswerBlob?: Blob | null
      queuedAnswerUrl?: string | null
    }) => {
      if (!playback || !playback.blob) return
      try {
        if (playback.type === 'filler') {
          if (playback.queuedAnswerBlob) answerAudioToPlayRef.current = playback.queuedAnswerBlob
          fillerAudioBlobRef.current = playback.blob || null
          await playFillerAudio(playback.blob)
          const audioEl = audioPlaybackRef.current
          if (audioEl && typeof playback.position === 'number') {
            const pos = playback.position as number
            if (audioEl.readyState >= 1) {
              try {
                audioEl.currentTime = pos
              } catch (err) {
                calculateErrorMessage(err)
              }
            } else {
              audioEl.addEventListener(
                'loadedmetadata',
                () => {
                  try {
                    audioEl.currentTime = pos
                  } catch (err) {
                    calculateErrorMessage(err)
                  }
                },
                {once: true}
              )
            }
          }
        } else if (playback.type === 'full') {
          answerAudioToPlayRef.current = playback.blob || null
          answerTextRef.current = playback.text || null
          answerTranscriptIdRef.current = playback.transcriptId || null
          setStatus('responding')
          setIsAiSpeaking(true)

          try {
            const audio = await audioPlayerRef.current.playBlob(playback.blob!, {
              onEnded: () => {
                audioPlaybackRef.current = null
                answerAudioToPlayRef.current = null
                setIsAiSpeaking(false)
                setStatus('listening')
              },
              onError: (e) => {
                calculateErrorMessage(e)
                audioPlaybackRef.current = null
                answerAudioToPlayRef.current = null
                setIsAiSpeaking(false)
                setStatus('listening')
              },
            })

            audioPlaybackRef.current = audio as HTMLAudioElement

            if (playback.transcriptId && playback.text) {
              syncTranscriptToAudio(audio as HTMLAudioElement, playback.transcriptId, playback.text)
            }

            if (typeof playback.position === 'number' && playback.position > 0) {
              if ((audio as HTMLAudioElement).readyState >= 1) {
                try {
                  ;(audio as HTMLAudioElement).currentTime = playback.position
                } catch (err) {
                  calculateErrorMessage(err)
                }
              } else {
                ;(audio as HTMLAudioElement).addEventListener(
                  'loadedmetadata',
                  () => {
                    try {
                      ;(audio as HTMLAudioElement).currentTime = playback.position!
                    } catch (err) {
                      calculateErrorMessage(err)
                    }
                  },
                  {once: true}
                )
              }
            }
          } catch (e) {
            calculateErrorMessage(e)
            setIsAiSpeaking(false)
            setStatus('listening')
          }

          try {
            if (playback.url) {
              try {
                URL.revokeObjectURL(playback.url)
              } catch (e) {
                calculateErrorMessage(e)
              }
              if (answerAudioUrlRef.current === playback.url) answerAudioUrlRef.current = null
              if (fillerAudioUrlRef.current === playback.url) fillerAudioUrlRef.current = null
            }
            if (playback.queuedAnswerUrl) {
              try {
                URL.revokeObjectURL(playback.queuedAnswerUrl)
              } catch (e) {
                calculateErrorMessage(e)
              }
              if (answerAudioUrlRef.current === playback.queuedAnswerUrl)
                answerAudioUrlRef.current = null
            }
          } catch (e) {
            calculateErrorMessage(e)
          }
        }
      } catch (e) {
        calculateErrorMessage(e)
      }
    },
    [playFillerAudio, syncTranscriptToAudio, setIsAiSpeaking, setStatus]
  )

  return useMemo(
    () => ({
      refs: {
        audioPlayer: audioPlayerRef.current,
        audioPlaybackRef,
        fillerAudioRef,
        answerAudioToPlayRef,
        fillerTranscriptIdRef,
        fillerTextRef,
        answerTextRef,
        answerTranscriptIdRef,
        answerCompletionCallbackRef,
      },
      playFillerAudio,
      playFullResponseAudio,
      stopAudioPlayback,
      stopFillerAudio,
      startUtteranceRecording,
      stopUtteranceRecording,
      stopRecordingImmediate,
      startProgressiveTranscript,
      stopProgressiveTranscript,
      pausePlayback,
      resumePlayback,
      progressiveManager: progressiveManagerRef.current,
      revokeStoredPlaybackUrls: () => {
        try {
          if (answerAudioUrlRef.current) {
            try {
              URL.revokeObjectURL(answerAudioUrlRef.current)
            } catch (e) {
              calculateErrorMessage(e)
            }
            answerAudioUrlRef.current = null
          }
          if (fillerAudioUrlRef.current) {
            try {
              URL.revokeObjectURL(fillerAudioUrlRef.current)
            } catch (e) {
              calculateErrorMessage(e)
            }
            fillerAudioUrlRef.current = null
          }
          answerAudioToPlayRef.current = null
          fillerAudioBlobRef.current = null
        } catch (e) {
          calculateErrorMessage(e)
        }
      },
    }),
    [
      playFillerAudio,
      playFullResponseAudio,
      stopAudioPlayback,
      stopFillerAudio,
      startUtteranceRecording,
      stopUtteranceRecording,
      stopRecordingImmediate,
      startProgressiveTranscript,
      stopProgressiveTranscript,
      pausePlayback,
      resumePlayback,
    ]
  )
}
